import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { config } from "../services/api";
import { Haulier, HAULIER_LABELS } from "../types";
import type { ServiceSurcharge } from "../types";

function emptyRow(haulier: Haulier): ServiceSurcharge {
  return {
    service_code: "",
    description: "",
    haulier,
    base_band: haulier === Haulier.PALLETWAYS ? "A" : null,
    surcharge_amount: 0,
    per_pallet: false,
  };
}

const HAULIER_TABS: Haulier[] = [Haulier.PALLETWAYS, Haulier.BOX_LOGISTICS, Haulier.TPN];

export default function SurchargesPage() {
  const queryClient = useQueryClient();
  const [haulier, setHaulier] = useState<Haulier>(Haulier.PALLETWAYS);
  const { data, isLoading, error } = useQuery({
    queryKey: ["surcharges", haulier],
    queryFn: () => config.getSurcharges(haulier),
  });

  const [rows, setRows] = useState<ServiceSurcharge[]>([]);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Seed local state from API data
  useEffect(() => {
    if (data?.surcharges) {
      setRows(data.surcharges);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (surcharges: ServiceSurcharge[]) =>
      config.saveSurcharges(haulier, surcharges),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["surcharges", haulier] });
      setFeedback({
        type: "success",
        message: `Saved ${result.count} surcharge${result.count === 1 ? "" : "s"} successfully.`,
      });
      setTimeout(() => setFeedback(null), 4000);
    },
    onError: (err: Error) => {
      setFeedback({ type: "error", message: err.message });
      setTimeout(() => setFeedback(null), 6000);
    },
  });

  function updateRow(index: number, field: keyof ServiceSurcharge, value: string | number | boolean) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow(haulier)]);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function handleSave() {
    saveMutation.mutate(rows);
  }

  const showBaseBand = haulier === Haulier.PALLETWAYS;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900">
          Service Surcharges
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure surcharge codes that map service prefixes to rate bands and
          additional charges.
        </p>
      </div>

      {/* Haulier tabs */}
      <div className="flex gap-1.5">
        {HAULIER_TABS.map((h) => (
          <button
            key={h}
            onClick={() => setHaulier(h)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              haulier === h
                ? "bg-emerald-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {HAULIER_LABELS[h]}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
        </div>
      )}

      {!isLoading && error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          Failed to load surcharges: {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && (
        <>
      {/* Feedback banner */}
      {feedback && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            feedback.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {feedback.message}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Code
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Description
              </th>
              {showBaseBand && (
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Base Band
                </th>
              )}
              <th className="px-4 py-3 text-left font-medium text-gray-600">
                Amount &pound;
              </th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">
                Per Pallet
              </th>
              <th className="px-4 py-3 text-center font-medium text-gray-600">
                Delete
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={showBaseBand ? 6 : 5}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  No surcharges configured. Click "Add Row" to begin.
                </td>
              </tr>
            )}

            {rows.map((row, idx) => (
              <tr key={idx} className="hover:bg-gray-50">
                {/* Code */}
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={row.service_code}
                    onChange={(e) =>
                      updateRow(idx, "service_code", e.target.value.toUpperCase())
                    }
                    placeholder="e.g. E01"
                    className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </td>

                {/* Description */}
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={row.description}
                    onChange={(e) =>
                      updateRow(idx, "description", e.target.value)
                    }
                    placeholder="Service description"
                    className="w-full min-w-[180px] rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </td>

                {/* Base Band */}
                {showBaseBand && (
                  <td className="px-4 py-2">
                    <select
                      value={row.base_band ?? "A"}
                      onChange={(e) =>
                        updateRow(idx, "base_band", e.target.value as "A" | "B")
                      }
                      className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    >
                      <option value="A">Economy (A)</option>
                      <option value="B">Premium (B)</option>
                    </select>
                  </td>
                )}

                {/* Amount */}
                <td className="px-4 py-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={row.surcharge_amount}
                    onChange={(e) =>
                      updateRow(
                        idx,
                        "surcharge_amount",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    className="w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-right focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </td>

                {/* Per Pallet */}
                <td className="px-4 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={row.per_pallet}
                    onChange={(e) =>
                      updateRow(idx, "per_pallet", e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </td>

                {/* Delete */}
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => removeRow(idx)}
                    className="rounded p-1 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
                    title="Remove row"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={addRow}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          + Add Row
        </button>
        <button
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? "Saving\u2026" : "Save"}
        </button>
      </div>
        </>
      )}
    </div>
  );
}
